import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ContactForm from "../components/ContactForm";
import { getImageAlt } from "../lib/imageMeta";
import { US_POINTS, TESTIMONIALS, CONTACT_INFO } from "../data";
import { applyPageMeta, PAGE_SEO_CONFIG } from "../lib/seoMeta";

export default function Home() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    // Set metadata for Forside (max 512px title, max 970px description)
    applyPageMeta(PAGE_SEO_CONFIG.home);

    // Fetch services from CMS
    fetch("https://api.dev.auxo.dk/public/content/services")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setServices(data);
        }
      })
      .catch((err) => console.error("Error fetching services:", err));
  }, []);

  const recentProjects = [
    {
      title: "Totalrenovering af murermestervilla",
      category: "Renovering",
      image: "https://assets.auxodev.dk/assets/1c3d2574-7326-4533-ae86-8c8f28cf4f21/koekkenrenovering-beige-elementer-traegulv.jpg",
    },
    {
      title: "Montering af energirigtige vinduer",
      category: "Vinduer & Døre",
      image: "https://assets.auxodev.dk/assets/1c3d2574-7326-4533-ae86-8c8f28cf4f21/montering-hvide-vinduer-og-terrassedoer.jpg",
    },
    {
      title: "Nyt tag og efterisolering",
      category: "Nyt tag",
      image: "https://assets.auxodev.dk/assets/1c3d2574-7326-4533-ae86-8c8f28cf4f21/tagrenovering-roedstenshus-tagsten-tagvinduer.jpg",
    },
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section with Contact Form */}
      <section className="relative bg-neutral-950 text-white py-16 lg:py-24 logo-line-container overflow-hidden">
        {/* Background Image with optimized Cloudflare resizing */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/1c3d2574-7326-4533-ae86-8c8f28cf4f21/toemrer-jg-mesterbyg-erslev-mors.jpg" 
            alt="Håndværker fra JG Mesterbyg ved firmabil i Erslev på Mors" 
            className="w-full h-full object-cover object-center opacity-70"
          />
          {/* Dark overlay to make text highly readable */}
          <div className="absolute inset-0 bg-neutral-950/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/70 via-neutral-950/30 to-transparent" />
        </div>

        {/* Discrete white lines inspired by the logo */}
        <div className="logo-line-diagonal top-1/4 left-0 opacity-15 z-10" />
        <div className="logo-line-diagonal top-2/3 right-0 opacity-10 z-10" />
        <div className="logo-line-horizontal bottom-0 left-0 opacity-15 z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Hero Text */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 px-4 py-2 rounded-full text-yellow-500 text-sm font-semibold">
                <i className="fa-solid fa-screwdriver-wrench" />
                Din lokale tømrermester
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none">
                Professionelt håndværk <br />
                <span className="text-yellow-500">der holder i generationer</span>
              </h1>
              <p className="text-lg text-neutral-300 max-w-2xl leading-relaxed">
                Hos <strong className="text-white font-semibold">JG Mesterbyg</strong> leverer vi kompromisløst tømrerarbejde. Vi hjælper dig sikkert i mål med alt fra tagudskiftning og solceller til køkkenrenovering og energivenlige vinduer.
              </p>
              
              {/* Trust indicators */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 pt-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-neutral-800 flex items-center justify-center text-yellow-500 text-xl">
                    <i className="fa-solid fa-shield-halved" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-white">Garanti</h4>
                    <p className="text-xs text-neutral-400">Dansk Byggeri Garanti</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-neutral-800 flex items-center justify-center text-yellow-500 text-xl">
                    <i className="fa-solid fa-star" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-white">5/5 Stjerner</h4>
                    <p className="text-xs text-neutral-400">Fremragende anmeldelser</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 col-span-2 sm:col-span-1">
                  <div className="w-12 h-12 rounded-xl bg-neutral-800 flex items-center justify-center text-yellow-500 text-xl">
                    <i className="fa-solid fa-user-check" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-white">Lokal tillid</h4>
                    <p className="text-xs text-neutral-400">Mors & Nordjylland</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Form */}
            <div className="lg:col-span-5 relative">
              {/* Background glow behind form */}
              <div className="absolute inset-0 bg-yellow-500/10 blur-3xl rounded-full -z-10" />
              <ContactForm source="Forside Hero Formular" buttonText="Få uforpligtende tilbud" />
            </div>
          </div>
        </div>
      </section>

      {/* USPs Section */}
      <section className="py-16 bg-neutral-100 text-neutral-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">
              Hvorfor vælge JG Mesterbyg?
            </h2>
            <p className="mt-4 text-lg text-neutral-600">
              Vi bygger på sunde værdier, ordentlighed og tæt dialog med vores kunder.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {US_POINTS.map((usp, idx) => (
              <div
                key={idx}
                className="bg-white p-6 rounded-2xl border border-neutral-200 shadow-sm flex flex-col items-start"
              >
                <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center text-yellow-600 text-xl mb-4">
                  <i className={`fa-solid ${usp.icon}`} />
                </div>
                <h3 className="text-lg font-bold text-neutral-900 mb-2">{usp.title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{usp.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white text-neutral-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4">
            <div>
              <span className="text-yellow-600 font-bold text-sm uppercase tracking-wider block mb-2">Hvad kan vi hjælpe med?</span>
              <h2 className="text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">
                Vores specialer & ydelser
              </h2>
            </div>
            <Link
              to="/fa-tilbud"
              className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-neutral-950 font-bold px-6 py-3 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer"
            >
              <span>Se alle og få tilbud</span>
              <i className="fa-solid fa-arrow-right" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Link
                key={service.id}
                to={`/ydelser/${service.slug._ || service.slug}`}
                className="bg-neutral-50 rounded-2xl border border-neutral-200 overflow-hidden shadow-sm flex flex-col group transition-all duration-300 hover:shadow-md hover:border-neutral-300 cursor-pointer"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${service.image[0]}`}
                    alt={getImageAlt(service.image[0], service.title)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {/* Diagonal yellow/black overlay separated by a white line (inspired by the logo) */}
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ 
                      backgroundImage: 'linear-gradient(135deg, rgba(234, 179, 8, 0.65) 0%, rgba(234, 179, 8, 0.65) 49.2%, rgba(255, 255, 255, 1) 49.2%, rgba(255, 255, 255, 1) 50.8%, rgba(23, 23, 23, 0.7) 50.8%, rgba(23, 23, 23, 0.7) 100%)' 
                    }}
                  />
                </div>
                <div className="p-6 flex-grow flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">{service.title}</h3>
                    <p className="text-sm text-neutral-600 leading-relaxed mb-6">{service.description}</p>
                  </div>
                  <div
                    className="inline-flex items-center gap-2 text-yellow-600 font-bold group-hover:text-yellow-700 transition-colors text-sm"
                  >
                    <span>Læs mere om {service.title.toLowerCase()}</span>
                    <i className="fa-solid fa-arrow-right text-xs transition-transform duration-200 group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}

            {/* CTA Box */}
            <div className="bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-2xl border border-neutral-800 p-8 flex flex-col justify-between text-white shadow-md relative overflow-hidden group min-h-[380px]">
              <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-yellow-500/10 rounded-full blur-2xl group-hover:bg-yellow-500/20 transition-all duration-300" />
              
              <div>
                <div className="w-12 h-12 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center text-yellow-500 text-xl mb-6">
                  <i className="fa-solid fa-calculator" />
                </div>
                <h3 className="text-2xl font-bold mb-3">Få en pris på dit næste projekt</h3>
                <p className="text-neutral-300 text-sm leading-relaxed mb-6">
                  Går du med bygge- eller renoveringsplaner? Fortæl os om dine ønsker, så skræddersyr vi et uforpligtende tilbud med en skarp pris.
                </p>
              </div>
              
              <div>
                <Link
                  to="/fa-tilbud"
                  className="inline-flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-neutral-950 font-bold px-6 py-3 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 w-full cursor-pointer"
                >
                  <span>Få et uforpligtende tilbud</span>
                  <i className="fa-solid fa-arrow-right" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Work Section */}
      <section className="py-20 bg-neutral-900 text-white relative logo-line-container">
        <div className="logo-line-diagonal top-1/3 left-0 opacity-10" />
        <div className="logo-line-horizontal top-1/2 left-0 opacity-15" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-yellow-500 font-bold text-sm uppercase tracking-wider block mb-2">Vores håndværk</span>
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              Udvalgte projekter vi er stolte af
            </h2>
            <p className="mt-4 text-neutral-400">
              Se et udpluk af det arbejde, vi for nylig har udført for vores glade kunder.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {recentProjects.map((project, idx) => (
              <div
                key={idx}
                className="bg-neutral-800 rounded-2xl border border-neutral-700 overflow-hidden shadow-lg"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${project.image}`}
                    alt={getImageAlt(project.image, `${project.title} - ${project.category}`)}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-yellow-500 text-neutral-950 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                    {project.category}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-white mb-4">{project.title}</h3>
                  <Link
                    to="/galleri"
                    className="inline-flex items-center gap-2 text-yellow-500 font-bold hover:text-yellow-400 transition-colors text-sm cursor-pointer"
                  >
                    <span>Se hele galleriet</span>
                    <i className="fa-solid fa-images" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-neutral-100 text-neutral-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-yellow-600 font-bold text-sm uppercase tracking-wider block mb-2">Kundeanmeldelser</span>
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              Det siger vores kunder om os
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, idx) => (
              <div
                key={idx}
                className="bg-white p-8 rounded-2xl border border-neutral-200 shadow-sm flex flex-col justify-between"
              >
                <div>
                  <div className="flex gap-1 text-yellow-500 mb-4">
                    {[...Array(t.rating)].map((_, i) => (
                      <i key={i} className="fa-solid fa-star" />
                    ))}
                  </div>
                  <p className="text-neutral-600 italic leading-relaxed text-sm mb-6">"{t.text}"</p>
                </div>
                <div className="border-t border-neutral-100 pt-4 flex items-center justify-between">
                  <span className="font-bold text-neutral-900 text-sm">{t.name}</span>
                  <span className="text-xs text-neutral-500">{t.city}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Information Section */}
      <section className="py-20 bg-white text-neutral-900 relative border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main SEO Header */}
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 px-4 py-1.5 rounded-full text-yellow-700 text-sm font-semibold mb-4">
              <i className="fa-solid fa-location-dot" />
              <span>Erslev & Mors</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-neutral-950 mb-6 leading-tight">
              Tømrer i Erslev og omegn til renovering af dit hjem
            </h2>
            <p className="text-lg text-neutral-700 leading-relaxed">
              Har du brug for en tømrer til at forny dit hjem? Hos JG Mesterbyg har vi base i Erslev på Mors og hjælper dig i byen og omegn. Vi udfører både mindre og større opgaver, fra vinduer og døre til renovering og nyt tag, med fokus på løsninger, der passer til huset og dine ønsker.
            </p>
          </div>

          {/* 4 Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Box 1: Hvad skal din tømrer hjælpe dig med? */}
            <div className="bg-neutral-50 rounded-2xl p-8 border border-neutral-200 hover:border-yellow-500/50 transition-all duration-300 shadow-sm flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-xl bg-yellow-500/10 text-yellow-600 flex items-center justify-center text-xl mb-5">
                  <i className="fa-solid fa-hammer" />
                </div>
                <h3 className="text-xl font-bold text-neutral-950 mb-3">
                  Hvad skal din tømrer hjælpe dig med?
                </h3>
                <p className="text-neutral-600 leading-relaxed text-sm">
                  Vi udfører tømrerarbejde og snedkerarbejde, når du vil ændre eller vedligeholde din bolig. Det kan være nye lofter, vægge og gulve, en tilbygning eller en terrasse. Vi hjælper dig med at afklare opgaven og vælge materialer, så du ved, hvad arbejdet skal omfatte, før vi går i gang.
                </p>
              </div>
            </div>

            {/* Box 2: Vinduer og døre */}
            <div className="bg-neutral-50 rounded-2xl p-8 border border-neutral-200 hover:border-yellow-500/50 transition-all duration-300 shadow-sm flex flex-col justify-between group">
              <div>
                <div className="w-12 h-12 rounded-xl bg-yellow-500/10 text-yellow-600 flex items-center justify-center text-xl mb-5 group-hover:bg-yellow-500 group-hover:text-neutral-950 transition-colors">
                  <i className="fa-solid fa-door-open" />
                </div>
                <h3 className="text-xl font-bold text-neutral-950 mb-3">
                  Vinduer og døre, der passer til din bolig
                </h3>
                <p className="text-neutral-600 leading-relaxed text-sm">
                  Trækker det fra vinduerne, eller er døren svær at lukke? Vi hjælper med{' '}
                  <Link
                    to="/ydelser/udskiftning-af-vinduer-og-doere"
                    className="font-semibold text-neutral-900 underline decoration-yellow-500 underline-offset-2 hover:text-yellow-600 transition-colors"
                  >
                    udskiftning af vinduer og døre
                  </Link>{' '}
                  samt montering af nye elementer. Vi måler op og sørger for isolering og fugning omkring dem. Du kan vælge træ, træ med aluminium eller plast alt efter husets stil og dit behov for vedligeholdelse.
                </p>
              </div>
              <div className="pt-6 border-t border-neutral-200/70 mt-6">
                <Link
                  to="/ydelser/udskiftning-af-vinduer-og-doere"
                  className="inline-flex items-center gap-2 text-yellow-600 font-bold hover:text-yellow-700 transition-colors text-sm group-hover:translate-x-1 duration-200"
                >
                  <span>Læs mere om udskiftning af vinduer og døre</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
              </div>
            </div>

            {/* Box 3: Renovering med fokus på din hverdag */}
            <div className="bg-neutral-50 rounded-2xl p-8 border border-neutral-200 hover:border-yellow-500/50 transition-all duration-300 shadow-sm flex flex-col justify-between group">
              <div>
                <div className="w-12 h-12 rounded-xl bg-yellow-500/10 text-yellow-600 flex items-center justify-center text-xl mb-5 group-hover:bg-yellow-500 group-hover:text-neutral-950 transition-colors">
                  <i className="fa-solid fa-house-chimney-window" />
                </div>
                <h3 className="text-xl font-bold text-neutral-950 mb-3">
                  Renovering med fokus på din hverdag
                </h3>
                <p className="text-neutral-600 leading-relaxed text-sm">
                  En renovering skal give dig en bolig, der fungerer bedre i hverdagen. Vi hjælper med{' '}
                  <Link
                    to="/ydelser/renovering"
                    className="font-semibold text-neutral-900 underline decoration-yellow-500 underline-offset-2 hover:text-yellow-600 transition-colors"
                  >
                    renovering af dit hus
                  </Link>
                  , ændringer i rumfordelingen og montering af et nyt køkken. Sammen gennemgår vi dine ønsker og de eksisterende forhold, så arbejdet med lofter, vægge og gulve hænger sammen med den måde, du bruger rummene på.
                </p>
              </div>
              <div className="pt-6 border-t border-neutral-200/70 mt-6">
                <Link
                  to="/ydelser/renovering"
                  className="inline-flex items-center gap-2 text-yellow-600 font-bold hover:text-yellow-700 transition-colors text-sm group-hover:translate-x-1 duration-200"
                >
                  <span>Læs mere om renovering</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
              </div>
            </div>

            {/* Box 4: Nyt tag */}
            <div className="bg-neutral-50 rounded-2xl p-8 border border-neutral-200 hover:border-yellow-500/50 transition-all duration-300 shadow-sm flex flex-col justify-between group">
              <div>
                <div className="w-12 h-12 rounded-xl bg-yellow-500/10 text-yellow-600 flex items-center justify-center text-xl mb-5 group-hover:bg-yellow-500 group-hover:text-neutral-950 transition-colors">
                  <i className="fa-solid fa-shield-halved" />
                </div>
                <h3 className="text-xl font-bold text-neutral-950 mb-3">
                  Hvornår giver et nyt tag mening?
                </h3>
                <p className="text-neutral-600 leading-relaxed text-sm">
                  Hvis taget er utæt eller har omfattende slid, kan et{' '}
                  <Link
                    to="/ydelser/nyt-tag"
                    className="font-semibold text-neutral-900 underline decoration-yellow-500 underline-offset-2 hover:text-yellow-600 transition-colors"
                  >
                    nyt tag
                  </Link>{' '}
                  være den rette løsning. Vi gennemgår taget og den bærende konstruktion, før vi vurderer opgaven. Valg af tagmateriale, undertag og eventuel efterisolering afhænger af huset. Prisen afhænger af tagets størrelse, tilstand og arbejdets omfang.
                </p>
              </div>
              <div className="pt-6 border-t border-neutral-200/70 mt-6">
                <Link
                  to="/ydelser/nyt-tag"
                  className="inline-flex items-center gap-2 text-yellow-600 font-bold hover:text-yellow-700 transition-colors text-sm group-hover:translate-x-1 duration-200"
                >
                  <span>Læs mere om nyt tag</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
              </div>
            </div>
          </div>

          {/* Bottom highlight section: Din lokale tømrer fra første snak til færdigt arbejde + Tilbud CTA */}
          <div className="bg-neutral-950 text-white rounded-3xl p-8 md:p-12 relative overflow-hidden shadow-xl">
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8 space-y-4">
                <span className="text-yellow-500 font-bold text-xs uppercase tracking-wider block">
                  Lokal tømrer i Nordjylland
                </span>
                <h3 className="text-2xl sm:text-3xl font-bold text-white">
                  Din lokale tømrer fra første snak til færdigt arbejde
                </h3>
                <p className="text-neutral-300 text-sm sm:text-base leading-relaxed">
                  Vi udfører tømrerarbejde i Nordjylland, blandt andet i Thisted og Nykøbing Mors og omegn. Vi kører også til opgaver i Skive og omegn. Som erhvervskunde kan du få hjælp til renovering af kontorer, butikker og andre erhvervslokaler.
                </p>
                <p className="text-neutral-300 text-sm sm:text-base leading-relaxed">
                  Du får et skriftligt tilbud, og vi aftaler opgavens indhold og tidsplan, før vi går i gang. Det giver dig overblik over pris og forløb. Ved køkkenprojekter kan vi også koordinere med andre faggrupper, herunder autoriserede installatører, så arbejdet følger en fælles plan.
                </p>
                <div className="pt-2 text-neutral-300 text-sm sm:text-base leading-relaxed">
                  <strong className="text-white">Vil du have et tilbud på tømrerarbejde?</strong> Kontakt JG Mesterbyg og fortæl, hvad du gerne vil have lavet. Send gerne billeder, mål og dine ønsker til materialer. Så har vi et godt udgangspunkt for en samtale om opgaven og et uforpligtende tilbud, der tager højde for dit projekt.
                </div>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-4">
                <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 text-center space-y-4">
                  <div className="w-12 h-12 mx-auto rounded-full bg-yellow-500/10 text-yellow-500 flex items-center justify-center text-xl">
                    <i className="fa-solid fa-file-signature" />
                  </div>
                  <h4 className="text-lg font-bold text-white">Klar til dit næste projekt?</h4>
                  <p className="text-xs text-neutral-400">
                    Få et uforpligtende tilbud skræddersyet til dine ønsker og din bolig.
                  </p>
                  <Link
                    to="/fa-tilbud"
                    className="inline-flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-neutral-950 font-bold px-6 py-3 rounded-xl shadow-md transition-all duration-200 w-full cursor-pointer text-sm"
                  >
                    <span>Få et uforpligtende tilbud</span>
                    <i className="fa-solid fa-arrow-right" />
                  </Link>
                  <a
                    href={`tel:${CONTACT_INFO.phone}`}
                    className="inline-flex items-center justify-center gap-2 bg-neutral-800 hover:bg-neutral-700 text-white font-medium px-6 py-3 rounded-xl transition-all duration-200 w-full text-sm"
                  >
                    <i className="fa-solid fa-phone text-yellow-500" />
                    <span>Ring {CONTACT_INFO.phoneDisplay}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-yellow-500 text-neutral-950 relative logo-line-container">
        <div className="logo-line-horizontal top-1/2 left-0 opacity-30" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 space-y-6">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Skal vi hjælpe dig med dit næste byggeprojekt?
          </h2>
          <p className="text-lg text-neutral-900 max-w-3xl mx-auto leading-relaxed">
            Uanset om det er stort eller småt, står vi klar med professionel rådgivning og et uforpligtende tilbud tilpasset dine ønsker.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-4">
            <Link
              to="/fa-tilbud"
              className="bg-neutral-950 hover:bg-neutral-900 text-white font-bold px-8 py-4 rounded-xl shadow-lg transition-all duration-200 flex items-center gap-2 cursor-pointer"
            >
              <span>Få et tilbud nu</span>
              <i className="fa-solid fa-file-invoice-dollar" />
            </Link>
            <a
              href={`tel:${CONTACT_INFO.phone}`}
              className="bg-white hover:bg-neutral-100 text-neutral-950 font-bold px-8 py-4 rounded-xl shadow-md transition-all duration-200 flex items-center gap-2 whitespace-nowrap"
            >
              <i className="fa-solid fa-phone" />
              <span>Ring os op: {CONTACT_INFO.phoneDisplay}</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
